import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle, AlertCircle, Mail } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';

type Status = 'verifying' | 'error';

/**
 * /account/email-confirmed
 *
 * Dedicated landing page for Supabase email verification links.
 * Supabase verifies the token and then redirects here with the session
 * already established (or an error in the URL hash).
 *
 * This page:
 * - Shows a brief "Verifying your email..." state while auth settles
 * - Redirects to /account?verified=1 on success so Account can show a one-time success toast
 * - Handles invalid/expired links with a friendly error and support/Account CTAs
 * - Avoids auth loops if the user is already logged in
 */
export default function AccountEmailConfirmed() {
  const navigate = useNavigate();
  const { user, session, loading: authLoading } = useAuth();
  const [status, setStatus] = useState<Status>('verifying');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    // Parse Supabase Auth error fragments, if any
    const hash = window.location.hash.startsWith('#')
      ? window.location.hash.slice(1)
      : window.location.hash;
    const params = new URLSearchParams(hash);

    const errorCode = params.get('error_code');
    const errorDescription = params.get('error_description');

    // If Supabase reported an error and the user is already logged in,
    // just show a small toast and send them to /account without getting stuck here.
    if (errorCode && !authLoading && user && session) {
      toast({
        title: 'Unable to verify email',
        description:
          errorDescription ||
          'The verification link is invalid or has expired. Please request a new verification email from your account.',
        variant: 'destructive',
      });
      navigate('/account', { replace: true });
      return;
    }

    // If there is an error and no active session, show the error view
    if (errorCode && !authLoading && (!user || !session)) {
      setStatus('error');
      setErrorMessage(
        errorDescription ||
          'We could not verify your email. The link may have expired. Please request a new verification email or contact support.'
      );
      return;
    }

    // Once auth has settled with a valid session, treat this as success
    if (!authLoading && user && session && status === 'verifying') {
      // Strip any auth fragments by navigating without a hash and let /account
      // show a clear one-time "Email verified" toast based on the verified=1 param.
      navigate('/account?verified=1', { replace: true });
    }

    // If auth has settled with no session and no explicit Supabase error,
    // show a generic error to avoid an infinite loading state.
    if (!authLoading && !user && !session && !errorCode && status === 'verifying') {
      setStatus('error');
      setErrorMessage(
        'We could not verify your email. The link may have expired. Please request a new verification email.'
      );
    }
  }, [authLoading, user, session, navigate, status]);

  if (status === 'verifying') {
    return (
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-md mx-auto">
          <Card>
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <Loader2 className="w-10 h-10 animate-spin text-primary" />
              </div>
              <CardTitle className="text-2xl">Verifying your email...</CardTitle>
              <CardDescription>
                This only takes a moment. Once verified, we&apos;ll take you to your account.
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center text-sm text-muted-foreground">
              If this page doesn&apos;t change after a few seconds, you can safely close it and return
              to your account.
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Error state
  return (
    <div className="container mx-auto px-4 py-16">
      <div className="max-w-md mx-auto">
        <Card>
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <AlertCircle className="w-10 h-10 text-red-600" />
            </div>
            <CardTitle className="text-2xl">We couldn&apos;t verify your email</CardTitle>
            <CardDescription>
              The verification link may have expired or already been used.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <Mail className="h-4 w-4" />
              <AlertDescription>
                {errorMessage ||
                  'Please request a new verification email from your account or contact support if the problem continues.'}
              </AlertDescription>
            </Alert>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                className="flex-1"
                onClick={() => {
                  navigate('/account', { replace: true });
                }}
              >
                Go to account
              </Button>
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  navigate('/support', { replace: true });
                }}
              >
                Contact Support
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}


